import { styled } from '@linaria/react';
import { Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { useFloraPage } from '../../../api/flora-pages-client/flora-pages-client.hooks';
import { Icon } from '../../../ui-library/icon/icon';
import { FloraPageEmbed } from './flora-page-embed';
import { FloraPageVisibilityMenu } from './flora-page-visibility-menu';
import { useFloraPageActions } from './use-flora-page-actions';

interface FloraPageRailPanelProps {
  pageId: string | undefined;
}

/**
 * The published-page rail panel: the shared `FloraPageEmbed` under a compact
 * header that mirrors the affordances the full-window viewer offers — an
 * owner-only visibility (Private/Shared) control and a pop-out that opens the
 * page full-window in a new tab. Kept separate from `FloraPageEmbed` so the
 * viewer route (which has its own floating chrome) does not double up on these
 * controls.
 *
 * @param {FloraPageRailPanelProps} props - The page to show in the rail
 * @returns {JSX.Element} The page rail panel (header controls + embedded page)
 */
export function FloraPageRailPanel({ pageId }: FloraPageRailPanelProps) {
  const { page } = useFloraPage(pageId);
  const { isOwner, toggleVisibility } = useFloraPageActions();
  const canManageVisibility = page ? isOwner(page) : false;
  const clipboard = useClipboard({ timeout: 1500 });

  useEffect(() => {
    if (clipboard.error) {
      notifications.show({ message: 'Could not copy the link. Please try again.', color: 'red' });
    }
  }, [clipboard.error]);

  const handleCopyLink = () => {
    if (!pageId) return;
    clipboard.copy(`${window.location.origin}/application/flora-agent/pages/${pageId}`);
  };

  return (
    <PanelRoot>
      <PanelHeader>
        {page && canManageVisibility && <FloraPageVisibilityMenu page={page} onToggleVisibility={toggleVisibility} />}
        <Spacer />
        {pageId && (
          <Tooltip label={clipboard.copied ? 'Copied!' : 'Copy link'} position="bottom" withArrow>
            <CopyLinkButton type="button" onClick={handleCopyLink} aria-label="Copy link">
              <Icon name={clipboard.copied ? 'check' : 'link'} size={16} />
            </CopyLinkButton>
          </Tooltip>
        )}
        {pageId && (
          <Tooltip label="Open in new tab" position="bottom" withArrow>
            <PopOutLink
              href={`/application/flora-agent/pages/${pageId}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open page in new tab"
            >
              <Icon name="open_in_new" size={16} />
            </PopOutLink>
          </Tooltip>
        )}
      </PanelHeader>
      <EmbedArea>
        <FloraPageEmbed pageId={pageId} />
      </EmbedArea>
    </PanelRoot>
  );
}

const PanelRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--base-white);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--sky-light);
  flex-shrink: 0;
`;

const Spacer = styled.div`
  flex: 1;
`;

const EmbedArea = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
`;

const railIconButtonStyles = `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-light);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    border-color: var(--sky-base);
    color: var(--ink-darkest);
    background: var(--sky-lighter);
  }
`;

const PopOutLink = styled.a`
  ${railIconButtonStyles}
`;

const CopyLinkButton = styled.button`
  ${railIconButtonStyles}
`;
